import type { FinanceReadiness } from '../../../executionOperations/types';
import type { ExecutionProgressHydration } from '../../../executionOperations/types';
import type { RouteViewModelControlTask } from '../../../../types/routeViewModel';
import type { RouteViewModelDocumentRequirement } from '../../../../types/routeViewModel';
import type { ExecutionIncident } from '../incidentFacade';
import { deriveFinanceReadinessView } from '../financeReadinessFacade';
import { deriveFinanceBlockers } from '../../../operationalDomain/finance/financeBlockers';
import {
  buildFinanceDocumentLines,
  financeLinesToReadinessFlags
} from '../../../operationalDomain/finance/documentReadiness';
import type { FinanceReadinessProjection } from './types';

export function buildFinanceReadinessProjection(params: {
  readiness: FinanceReadiness | null | undefined;
  hydration?: ExecutionProgressHydration | null;
  openDocumentTasks?: number;
  incidents?: ExecutionIncident[];
  documentRequirements?: RouteViewModelDocumentRequirement[];
  controlTasks?: RouteViewModelControlTask[];
}): FinanceReadinessProjection {
  const view = deriveFinanceReadinessView(params);
  const blockers = deriveFinanceBlockers({
    readiness: params.readiness,
    incidents: params.incidents,
    openDocumentTasks: params.openDocumentTasks,
    hydrationBlockers: params.hydration?.progress.blockers
  });

  const documentLines = buildFinanceDocumentLines({
    requirements: params.documentRequirements ?? [],
    controlTasks: params.controlTasks ?? []
  });
  const docFlags = financeLinesToReadinessFlags(documentLines);

  const hasDocContext =
    (params.documentRequirements?.length ?? 0) > 0 ||
    (params.controlTasks?.length ?? 0) > 0;

  return {
    invoiceReady: hasDocContext ? docFlags.invoiceReady : view.invoiceReady,
    updReady: hasDocContext ? docFlags.updReady : view.updReady,
    originalsReady: hasDocContext ? docFlags.originalsReady : view.originalsReady,
    documentLines,
    blockedReasons: view.blockReasons.length > 0 ? view.blockReasons : view.reasons,
    blockers,
    headline: view.headline,
    detail: view.detail,
    allReady: hasDocContext
      ? docFlags.invoiceReady && docFlags.updReady && docFlags.originalsReady && !view.blocked
      : view.allReady,
    blocked: view.blocked
  };
}
